import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TimePeriod } from 'ngx-daterangepicker-material/daterangepicker.component';
import {
  DataBroadcast,
  DataBroadcastModel,
  FilterFields,
  StatusField,
  ZonePlanModel,
  ZonePlanTrackerEntityCountPayloadModel,
  ZoneViewer,
  ZoneViewerType,
} from 'src/app/model';
import {
  DataService,
  GeneralService,
  ServicecallService,
} from 'src/app/services';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-page-controls',
  templateUrl: './page-controls.component.html',
  styleUrl: './page-controls.component.scss',
})
export class PageControlsComponent implements OnInit, OnDestroy {
  @Input()
  set headerStatus(value: any) {
    if (!value?.content) this._getConfiguration();
    else {
      this.statusDetails = value?.content ?? [];
      this.statusColorMapping = value?.colorMapping ?? {};
    }
  }

  @Input()
  set zone(zonePlan: ZonePlanModel) {
    this._zone = zonePlan;
    this._initialize();
  }

  get zone() {
    return this._zone;
  }

  @Input() view: ZoneViewerType = ZoneViewer.TREE_VIEW;
  @Input() filterCount: number = 0;
  @Input() treeViewFilteredList: string[] = [];
  @Input() filterVisible: boolean = true;

  @Output() backToTreeView = new EventEmitter<any>();
  @Output() toggleViewer = new EventEmitter<any>();
  @Output() filterOptions = new EventEmitter<any>();

  public headerCount = [
    {
      imgUrl: '../../../../assets/images/devicesCount.svg',
      count: 0,
      name: 'Devices Count',
      _id: 'deviceCount',
    },
    {
      imgUrl: '../../../../assets/images/assetsCount.svg',
      count: 0,
      name: 'Assets Count',
      _id: 'assetCount',
    },
  ];

  // Status
  public statusDetails: any = [];
  public statusColorMapping: any = [];
  public displayDate: string = '';

  // filter
  public filterFields: FilterFields;

  // header count
  private _zone: ZonePlanModel;

  // date picker
  public datepickerForm: FormGroup;
  private _selectedDates = {
    from: null,
    to: null,
  };
  public selectedDateRangeLabel: string = 'Today';

  readonly ZONE_VIEWER = ZoneViewer;
  private _dataServiceEventSubscription: Subscription;
  private _getTrackerEntityCountSubscription: Subscription;
  public countDetails = {
    deviceCount: null,
    assetCount: null,
  };
  // Configuration
  private _tenantGenericConfig: any;
  private _floorZoneIds: string[];

  constructor(
    public _service: ServicecallService,
    private _generalService: GeneralService,
    private _dataSer: DataService,
    private _fb: FormBuilder,
    private _datePipe: DatePipe
  ) {
    this._dataServiceEventSubscription = this._dataSer.events.subscribe({
      next: (broadcast: DataBroadcastModel) => {
        if (broadcast?.name === DataBroadcast.SELECTED_DATE_RANGE) {
          this.selectedDateRangeLabel = broadcast?.args?.[0]?.label ?? 'Today';
        } else if (
          broadcast?.name === DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED
        ) {
          this._getTrackerEntityCount();
        } else if (broadcast?.name === DataBroadcast.FLOOR_ZONE) {
          this._floorZoneIds = broadcast?.args?.[0] ?? [];
        }
      },
    });
  }

  ngOnInit(): void {
    // Set configuration
    this._getConfiguration();
    this._initialize();
  }
  ngOnDestroy(): void {
    this._dataServiceEventSubscription?.unsubscribe();
    this._getTrackerEntityCountSubscription?.unsubscribe();
  }

  /**
   * Sets configuration
   */
  private _getConfiguration() {
    this.filterFields =
      this._service.getConfiguration('entityFilter')?.filterPlanogramFields ??
      {};
    // Validate and map status field values
    const _statusMapping =
      this.filterFields?.['status']?.fieldValues?.reduce(
        (acc: Record<string, string>, item: StatusField) => {
          acc[item.value] = item.name;
          return acc;
        },
        {}
      ) || {};
    // Ensure all status types are represented
    const allStatusValues =
      this.filterFields?.[`status`]?.fieldValues?.map((f) => f.value) || [];
    this.statusDetails = [
      { trackedState: null, count: 0, name: 'Total' },
      ...allStatusValues.map((value) => ({
        trackedState: value,
        name: _statusMapping[value] || 'Unknown',
        count: 0,
      })),
    ];
  }
  /**
   * Initialize API call
   */
  private _initialize() {
    this._tenantGenericConfig = this._service.getConfiguration(
      'tenantGenericConfig'
    )
      ? this._service.getConfiguration('tenantGenericConfig')
      : null;
    const [startTime] = this._generalService?.getStartEndTimeByDate();
    this.datepickerForm = this._fb.group({
      thisdate: {
        startDate: startTime,
        endDate: dayjs(),
      },
    });
    this._generalService.setPageControlDateSelection(
      this.datepickerForm?.value?.thisdate
    );
    this.statusColorMapping = this._generalService.createColorMapping(
      this.filterFields?.[`status`]?.fieldValues
    );
    this._updateHeaderCount(this.zone);
  }

  /**
   * Trasform the count with input zone value
   */
  private _updateHeaderCount(data?: ZonePlanModel): void {
    if (!this.headerCount) return;
    const sourceData = data || this.countDetails;
    this.headerCount = this.headerCount.map((item) => ({
      ...item,
      count:
        sourceData?.[item?._id] !== undefined
          ? sourceData[item._id]
          : item?.count,
    }));
  }

  /**
   * track by for performance improvement
   * @param {PlanogramEntity} item - item details
   * @return Unique identifier for tracking changes for summary
   */
  public trackByTrackedState(item: any): number {
    return item.trackedState;
  }

  /**
   * track by for performance improvement
   * @param {numer} index - index
   * @return Unique identifier for tracking changes for breadcrumb
   */
  trackByCrumbs(index: number): number {
    return index;
  }

  /**
   * track by for performance improvement
   * @param {numer} index - index
   * @return Unique identifier for tracking changes for headercount
   */
  trackByCount(index: number): number {
    return index;
  }

  /**
   * Handles filter
   * @param isOpen
   */
  public handleFilter() {
    this.filterOptions.emit({ isOpen: true });
  }

  /**
   * Determines whether back button click on
   *  @param view
   */
  onBackButtonClick(view: string) {
    this.backToTreeView.emit(view);
  }

  /**
   * toggle view between planogram and tree
   */
  toggleView(): void {
    if (this.view === ZoneViewer.PLAN_VIEW) {
      this.view = ZoneViewer.PLANOGRAM_VIEW;
      this.toggleViewer.emit('PLANOGRAM_VIEW');
    } else {
      this.view = ZoneViewer.PLAN_VIEW;
      this.toggleViewer.emit('PLAN_VIEW');
    }
  }

  /**
   * braodcast table drawer open event
   */
  handleTableView() {
    this._dataSer.broadcast('tableDrawerOpen');
  }

  private _getTimestampInMillisecond(date: Date): number {
    return date ? new Date(date)?.getTime() : 0;
  }

  saveTime(data?: any) {
    if (data) {
      let dateRange: TimePeriod = data;
      const offsetTime = new Date().getTimezoneOffset() * 60 * 1000;
      this._selectedDates.from =
        this._getTimestampInMillisecond(dateRange?.startDate?.toDate()) +
        offsetTime;
      this._selectedDates.to =
        this._getTimestampInMillisecond(dateRange?.endDate?.toDate()) +
        offsetTime;
      this.datepickerForm.setValue({
        thisdate: {
          startDate: this._selectedDates.from,
          endDate: this._selectedDates.to,
        },
      });
      const startFormatted = this._datePipe.transform(
        this.datepickerForm?.value?.thisdate?.startDate,
        'short'
      );
      const endFormatted = this._datePipe.transform(
        this.datepickerForm?.value?.thisdate?.endDate,
        'short'
      );
      this.displayDate = `${startFormatted} - ${endFormatted}`;
    }
    this._generalService.setPageControlDateSelection(
      this.datepickerForm?.value?.thisdate
    );
    this._dataSer.broadcast('pageControlDateChange');
  }

  /**
   * Gets tracker entity count payload
   * @returns tracker entity count payload
   */
  private _getTrackerEntityCountPayload(): ZonePlanTrackerEntityCountPayloadModel {
    const trackerCategories: string[] = [];
    const entityCategorySet = new Set<string>();
    this._tenantGenericConfig?.tracker?.floorPlan?.visibleCategory?.forEach(
      (config) => {
        if (config.includeInTracker !== false) {
          trackerCategories.push(config.category);
        }
        config.subCategoryTracked?.forEach((subCat) => {
          entityCategorySet.add(subCat);
        });
        if (config.includeAsEntity === true) {
          entityCategorySet.add(config.category);
        }
      }
    );
    return {
      trackerCategory: trackerCategories,
      entityCategory: Array.from(entityCategorySet),
      siteId: this._service.getSiteId(),
      includeUnspecifiedEntities:
        this._tenantGenericConfig?.tracker?.floorPlan?.trackerCountConfig
          ?.includeUnspecifiedEntities ?? false,
      entities:
        this._tenantGenericConfig?.tracker?.floorPlan?.trackerCountConfig
          ?.entities ?? [],
    } as ZonePlanTrackerEntityCountPayloadModel;
  }

  _getTrackerEntityCount(): void {
    this._getTrackerEntityCountSubscription = this._service
      .trackerEntityCount(this._getTrackerEntityCountPayload())
      .subscribe({
        next: (response: any[]) => {
          this.countDetails = this._calculateCountDetails(response);
          this._updateHeaderCount();
        },
        error: () => {
          this.countDetails = {
            deviceCount: null,
            assetCount: null,
          };
        },
      });
  }

  private _calculateCountDetails(response: any[]): {
    deviceCount: number;
    assetCount: number;
  } {
    if (!response?.length) {
      return { deviceCount: 0, assetCount: 0 };
    }
    const targetZoneIds = this._getTargetZoneIds();
    if (!targetZoneIds.size) {
      return { deviceCount: 0, assetCount: 0 };
    }
    return response
      .filter((item) => targetZoneIds.has(item?.zoneId))
      .reduce(
        (acc, item) => ({
          deviceCount: acc.deviceCount + (item?.trackerCount || 0),
          assetCount: acc.assetCount + (item?.entityCount || 0),
        }),
        { deviceCount: 0, assetCount: 0 }
      );
  }

  private _getTargetZoneIds(): Set<string> {
    const zoneIds = new Set<string>();
    if (this.zone?._id) {
      zoneIds.add(this.zone._id);
    }
    if (this._floorZoneIds?.length) {
      this._floorZoneIds.forEach((id) => zoneIds.add(id));
    }
    return zoneIds;
  }
}
